import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { Button, Panel } from 'react-bootstrap';
import moment from 'moment';
import ReactTable from 'react-table';

import UserModalEnhanced from './UserModalEnhanced';

import {
  defaultTableFiltering,
  tableStringSorting,
  dateTableSorting,
} from '../../../../utils';

const propTypes = exact({
  users: PropTypes.array.isRequired,
  openEntryModal: PropTypes.func.isRequired,
  showUserEntryModal: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  /* eslint-disable react/no-unused-prop-types */
  excludeTerminatedUsers: PropTypes.bool.isRequired,
  /* eslint-enable react/no-unused-prop-types */
});


const UserTable = (props) => {
  const {
    users, openEntryModal, showUserEntryModal, loading,
  } = props;


  const builtFullnameSortingObject = (inputObject) => {
    if (!inputObject) {
      return {
        fullname: '',
      };
    }
    return inputObject;
  };

  const noData = () => {
    if (loading) {
      return null;
    }
    return <div className="rt-noData">No rows found</div>;
  };


  return (
    <React.Fragment>
      <ReactTable
        data={users}
        filterable
        loading={loading}
        NoDataComponent={noData}
        defaultFilterMethod={(filter, row) => {
          return defaultTableFiltering(filter, row);
        }}
        SubComponent={(row) => {
          const user = row.original;
          return (
            <div>
              <Panel header="Employee Details">
                <div>Role: {user.role}</div>
                <div>Cell #: {user.cell_number}</div>
                <div>Email: {user.email}</div>
              </Panel>
            </div>
          );
        }}
        columns={[
          {
            Header: 'First Name',
            id: 'firstname',
            accessor: (e) => { return e.firstname; },
          },
          {
            Header: 'Last Name',
            id: 'lastname',
            accessor: (e) => { return e.lastname; },
          },
          {
            Header: 'Supervisor Name', /* name, super_id */
            id: 'supervisor',
            /* eslint-disable consistent-return */
            accessor: (e) => { return e.supervisor; },
            sortMethod: (a, b) => {
              const objectA = builtFullnameSortingObject(a);
              const objectB = builtFullnameSortingObject(b);
              return tableStringSorting(objectA, objectB, 'fullname');
            },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: 'fullname',
                value: filter.value,
              };

              if (row.supervisor) {
                return defaultTableFiltering(customFilterObj, row.supervisor);
              }
            },
            Cell: (row) => {
              const hasSuper = !!row.value;
              return (
                <div>
                  {hasSuper && <span> {row.value.fullname }</span> }
                  {!hasSuper && <span> None</span>}
                </div>
              );
            },
          },
          {
            Header: 'District',
            id: 'default_district',
            accessor: (e) => { return e.default_district; },
            sortMethod: (a, b) => {
              return tableStringSorting(a, b, 'name');
            },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: 'name',
                value: filter.value,
              };
              return defaultTableFiltering(customFilterObj, row.default_district);
            },
            Cell: (row) => {
              return (
                <div>
                  { row.original.default_district.name }
                </div>
              );
            },
          },
          {
            Header: 'Department',
            id: 'default_department',
            accessor: (e) => { return e.default_department; },
            sortMethod: (a, b) => {
              return tableStringSorting(a, b, 'name');
            },
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: 'name',
                value: filter.value,
              };
              return defaultTableFiltering(customFilterObj, row.default_department);
            },
            Cell: (row) => {
              return (
                <div>
                  {row.original.default_department.name}
                </div>
              );
            },
          },
          {
            Header: 'Role',
            id: 'role',
            accessor: (e) => { return e.role; },
          },
          {
            Header: 'Payroll Type',
            id: 'payroll_type',
            accessor: (e) => { return e.payroll_type; },
          },
          {
            Header: 'Login Enabled',
            id: 'enabled',
            accessor: (e) => { return e.enabled; },
            Cell: (row) => {
              const isEnabled = String(row.value);
              return (
                <div>
                  {isEnabled}
                </div>
              );
            },
          },
          {
            Header: 'Has Payment Id',
            id: 'payment_system_id',
            accessor: (e) => { return e.payment_system_id; },
            Cell: (row) => {
              const { payment_system_id: paymentId } = row.original;
              const hasPaymentId = String(!!paymentId);
              return (
                <div>
                  {hasPaymentId}
                </div>
              );
            },
          },
          {
            Header: 'Hire Date',
            id: 'hired_at',
            accessor: (e) => { return e.hired_at; },
            sortMethod: (a, b) => {
              return dateTableSorting(a, b);
            },
            Cell: (row) => {
              const rowDate = !!row.value;
              return (
                <div>
                  {rowDate && <span> {moment(row.value.iso8601).format('MM/DD/YYYY') }</span> }
                </div>
              );
            },
          },
          {
            Header: 'Termination Date',
            id: 'terminated_at',
            accessor: (e) => { return e.terminated_at; },
            sortMethod: (a, b) => {
              return dateTableSorting(a, b);
            },
            Cell: (row) => {
              const rowDate = !!row.value;
              return (
                <div>
                  {rowDate && <span> {moment(row.value.iso8601).format('MM/DD/YYYY') }</span> }

                </div>
              );
            },
          },
          {
            Header: 'Actions',
            id: 'actions',
            filterable: false,
            sortable: false,
            Cell: (row) => {
              return (
                <div>
                  <Button
                    bsStyle="info"
                    onClick={() => {
                      /* TODO: Remove Formatting the date from the query
                      to match that of the maskedInput in the form
                      */
                      const modalObject = Object.assign({}, row.original);
                      if (row.original.hired_at) {
                        modalObject.hired_at = moment(row.original.hired_at.iso8601).format('MM/DD/YYYY');
                      }
                      if (row.original.terminated_at) {
                        modalObject.terminated_at = moment(row.original.terminated_at.date).format('MM/DD/YYYY');
                      }
                      openEntryModal(modalObject);
                    }}
                  >Edit
                  </Button>
                </div>
              );
            },
          },
        ]}
        defaultPageSize={10}
        className="-striped -highlight"
      />
      {showUserEntryModal && <UserModalEnhanced /> }
    </React.Fragment>
  );
};

UserTable.propTypes = propTypes;

export default UserTable;
